import { films, categories, directors } from "@/mocks/films";
import type { Film, Category } from "@/mocks/films";

export type SortKey = "heat" | "newest" | "oldest";

export type FilterState = {
  category: string;
  year: string;
  director: string;
  sort: SortKey;
};

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "按热度" },
  { key: "newest", label: "最新上映" },
  { key: "oldest", label: "年代最早" },
];

export { films, categories, directors };
export type { Film, Category };

export function getFilmById(id: string | undefined): Film | undefined {
  if (!id) return undefined;
  return films.find((film) => film.id === id);
}

export function getCategoryByKey(key: string): Category | undefined {
  return categories.find((category) => category.key === key);
}

export function countByCategory(key: string): number {
  return films.filter((film) => film.categories.includes(key)).length;
}

export function allYears(): number[] {
  return Array.from(new Set(films.map((film) => film.year))).sort((a, b) => b - a);
}

export function applyFilters(state: FilterState): Film[] {
  const result = films.filter((film) => {
    const categoryMatch = state.category === "all" || film.categories.includes(state.category);
    const yearMatch = state.year === "all" || String(film.year) === state.year;
    const directorMatch = state.director === "all" || film.director === state.director;
    return categoryMatch && yearMatch && directorMatch;
  });

  return result.sort((a, b) => {
    if (state.sort === "newest") return b.year - a.year;
    if (state.sort === "oldest") return a.year - b.year;
    return b.heat - a.heat;
  });
}

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "");
}

export function searchFilms(keyword: string): Film[] {
  const query = normalize(keyword);
  if (!query) return [];
  return films
    .filter((film) => {
      const haystack = [
        film.title,
        film.enTitle,
        film.director,
        film.region,
        film.tagline,
        ...film.genres,
        ...film.cast,
        ...film.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return normalize(haystack).includes(query);
    })
    .sort((a, b) => b.heat - a.heat);
}

export function getRelatedFilms(film: Film, limit = 4): Film[] {
  return films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      const shared = item.categories.filter((key) => film.categories.includes(key)).length;
      const sameDirector = item.director === film.director ? 2 : 0;
      return { item, score: shared * 3 + sameDirector };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function featuredFilms(): Film[] {
  return films.filter((film) => film.featured);
}

export function hotFilms(limit = 12): Film[] {
  return [...films].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function formatDuration(minutes: number): string {
  return `${minutes} 分钟`;
}